import { useEffect, useRef, useState } from 'react';
import type { Section } from '@/types/chat';

/**
 * Typing a reply out: a cursor that runs through its sections in order. Text counts by its
 * characters; a block that comes whole (a group of cards, related pages…) counts as CARD
 * characters, a breath before the text goes on. A card's explanation is typed inside it.
 */
const CARD = 40;

const isText = (s: Section): s is Extract<Section, { body_md: string }> => typeof (s as { body_md?: unknown }).body_md === 'string';

const weight = (s: Section): number =>
  isText(s) ? s.body_md.length : s.type === 'product_recommendations' ? CARD + (s.explanation?.length ?? 0) : CARD;

export const flowLength = (flow: Section[]): number => flow.reduce((n, s) => n + weight(s), 0);

/**
 * The sections as far as the cursor has got. Those already whole are the very same objects,
 * so only the one being typed re-renders from one frame to the next.
 */
export function cutFlow(flow: Section[], shown: number): Section[] {
  const out: Section[] = [];
  let left = shown;
  for (const s of flow) {
    if (left <= 0) break;
    const w = weight(s);
    if (left >= w) out.push(s);
    else if (isText(s)) out.push({ ...s, body_md: cutMarkdown(s.body_md, left) });
    else if (s.type === 'product_recommendations' && s.explanation)
      out.push({ ...s, explanation: cutMarkdown(s.explanation, left - CARD) });
    else out.push(s);
    left -= w;
  }
  return out;
}

/**
 * The first n characters of some markdown, cut so that nothing shows half-made: a link comes
 * whole rather than as raw [text](url) for a moment, and an open **bold** is closed for now.
 */
export function cutMarkdown(md: string, n: number): string {
  if (n <= 0) return '';
  if (n >= md.length) return md;
  for (const m of md.matchAll(/!?\[[^\]\n]*\]\([^)\n]*\)/g)) {
    if (m.index < n && n < m.index + m[0].length) n = m.index + m[0].length;
  }
  if (md[n - 1] === '*' && md[n] === '*') n += 1;
  const cut = md.slice(0, n);
  if ((cut.match(/\*\*/g)?.length ?? 0) % 2 === 0) return cut;
  return cut.endsWith('**') ? cut.slice(0, -2) : `${cut}**`;
}

/**
 * How much of a reply of `total` is shown: all of it (a reply from history, or reduced
 * motion), or — `live` — a cursor typing it out. Its pace is by time, not by frame, so slow
 * frames don't slow it: it grows with the backlog (a typewriter at the tail, a quick catch-up
 * after a burst of lines) and is never under the whole reply in 1.5s.
 */
export function useTypewriter(total: number, live: boolean): number {
  const [shown, setShown] = useState(() => (live && !prefersReducedMotion() ? 0 : Infinity));
  const lastFrame = useRef<number | null>(null);
  useEffect(() => {
    if (shown >= total) {
      lastFrame.current = null; // waiting for more: the next stretch starts afresh
      return;
    }
    const frame = requestFrame((now) => {
      const ms = now === undefined || lastFrame.current === null ? 16 : Math.min(250, now - lastFrame.current);
      if (now !== undefined) lastFrame.current = now;
      setShown((s) => Math.min(total, s + Math.max(1, Math.round(ms * pace(total - s, total)))));
    });
    return () => cancelFrame(frame);
  }, [shown, total]);
  return shown;
}

/** characters per ms */
function pace(remaining: number, total: number): number {
  const perFrame = remaining > 320 ? 12 : remaining > 160 ? 8 : remaining > 72 ? 4 : remaining > 24 ? 2 : 1;
  return Math.max(perFrame / 16, total / 1500);
}

export function prefersReducedMotion(): boolean {
  return typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// jsdom (tests) may have no requestAnimationFrame; without one, no timestamp: a frame is 16ms
function requestFrame(callback: (now?: number) => void): number {
  return typeof globalThis.requestAnimationFrame === 'function'
    ? globalThis.requestAnimationFrame(callback)
    : (globalThis.setTimeout(() => callback(), 16) as unknown as number);
}

function cancelFrame(frame: number): void {
  if (typeof globalThis.cancelAnimationFrame === 'function') globalThis.cancelAnimationFrame(frame);
  else globalThis.clearTimeout(frame);
}
